/**
 * RealTimeAlerts - Component for real-time push notifications
 * Uses Server-Sent Events (SSE) to receive alerts from backend
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { ScrollArea } from './ui/scroll-area';
import { 
  Bell, 
  BellRing,
  AlertTriangle, 
  Package, 
  Clock,
  Calendar,
  X,
  Volume2,
  VolumeX,
  Check
} from 'lucide-react';
import { toast } from 'sonner';

const RealTimeAlerts = () => {
  const { api, token, isAuthenticated } = useAuth();
  const location = useLocation();
  const [alerts, setAlerts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const abortControllerRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);
  const pollIntervalRef = useRef(null);

  // Only show stock toasts on stock-related pages
  const isStockPage = ['/estoque', '/sku', '/aprovisionamento', '/dispensacao'].some(p => location.pathname.startsWith(p));

  // Play notification sound
  const playSound = useCallback((type = 'warning') => {
    if (!soundEnabled) return;
    
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.connect(gain);
      gain.connect(ctx.destination);
      
      if (type === 'critical') {
        // Urgent sound - two quick beeps
        osc.type = 'square';
        osc.frequency.value = 880;
        gain.gain.value = 0.3;
        osc.start();
        osc.stop(ctx.currentTime + 0.1);
        
        setTimeout(() => {
          const osc2 = ctx.createOscillator();
          const gain2 = ctx.createGain();
          osc2.connect(gain2);
          gain2.connect(ctx.destination);
          osc2.type = 'square';
          osc2.frequency.value = 880;
          gain2.gain.value = 0.3;
          osc2.start();
          osc2.stop(ctx.currentTime + 0.1);
        }, 150);
      } else {
        // Regular notification sound
        osc.type = 'sine';
        osc.frequency.value = 523.25; // C5
        gain.gain.value = 0.2;
        osc.start();
        osc.stop(ctx.currentTime + 0.15);
      }
    } catch (e) {
      // Sound playback not available
    }
  }, [soundEnabled]);

  // Show toast notification - only on stock-related pages
  const showToastNotification = useCallback((alert) => {
    if (!isStockPage) return;
    const icon = alert.severity === 'critical' ? '🚨' : '⚠️';
    
    if (alert.severity === 'critical') {
      toast.error(`${icon} ${alert.title}`, {
        description: alert.message,
        duration: 8000,
        action: {
          label: 'Ver',
          onClick: () => setIsOpen(true)
        }
      });
    } else {
      toast.warning(`${icon} ${alert.title}`, {
        description: alert.message,
        duration: 5000
      });
    }
  }, [isStockPage]);

  // Connect to SSE stream
  const connectToStream = useCallback(() => {
    if (!isAuthenticated || !token) return;
    
    // Abort any existing connection
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    const controller = new AbortController();
    abortControllerRef.current = controller;
    
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const sseUrl = `${backendUrl}/api/alerts/stream`;
    
    const fetchSSE = async () => {
      try {
        const response = await fetch(sseUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'text/event-stream'
          },
          signal: controller.signal
        });
        
        if (!response.ok) {
          throw new Error(`SSE response ${response.status}`);
        }
        
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        if (isMountedRef.current) setIsConnected(true);
        
        let buffer = '';
        
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';
          
          for (const line of lines) {
            if (line.startsWith('event: ')) continue;
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                
                if (data.type === 'heartbeat') {
                  if (isMountedRef.current) {
                    setUnreadCount(data.critical + data.warning);
                  }
                } else {
                  if (isMountedRef.current) {
                    setAlerts(prev => {
                      const exists = prev.some(a => 
                        (a.item_id && a.item_id === data.item_id) || 
                        (a.task_id && a.task_id === data.task_id) ||
                        (a.lote_id && a.lote_id === data.lote_id)
                      );
                      if (exists) return prev;
                      
                      playSound(data.severity);
                      showToastNotification(data);
                      
                      return [data, ...prev].slice(0, 20);
                    });
                    setUnreadCount(prev => prev + 1);
                  }
                }
              } catch { /* ignore parse errors */ }
            }
          }
        }
      } catch (error) {
        // Only log if not aborted (abort = intentional disconnect)
        if (error.name !== 'AbortError' && isMountedRef.current) {
          if (isMountedRef.current) setIsConnected(false);
          // Silent reconnect after delay — fall back to polling
          reconnectTimeoutRef.current = setTimeout(() => {
            if (isMountedRef.current) connectToStream();
          }, 30000);
        }
      }
    };
    
    fetchSSE();
  }, [isAuthenticated, token, playSound, showToastNotification]);

  // Load initial alerts
  const loadCurrentAlerts = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      const response = await api.get('/alerts/current');
      setAlerts(response.data.alerts || []);
      setUnreadCount(response.data.summary?.critical + response.data.summary?.warning || 0);
    } catch (error) {
      console.error('Failed to load alerts:', error);
    }
  }, [api, isAuthenticated]);

  // Connect on mount
  useEffect(() => {
    isMountedRef.current = true;
    
    if (isAuthenticated) {
      loadCurrentAlerts();
      connectToStream();
      
      // Polling fallback: refresh alerts every 60s via REST
      pollIntervalRef.current = setInterval(() => {
        if (isMountedRef.current) loadCurrentAlerts();
      }, 60000);
    }
    
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [isAuthenticated, connectToStream, loadCurrentAlerts]);

  // Dismiss alert
  const dismissAlert = async (alert) => {
    const alertId = alert.item_id || alert.task_id || alert.lote_id;
    const alertType = alert.type;
    
    try {
      await api.post(`/alerts/dismiss/${alertType}/${alertId}`);
      setAlerts(prev => prev.filter(a => 
        (a.item_id !== alert.item_id) && 
        (a.task_id !== alert.task_id) &&
        (a.lote_id !== alert.lote_id)
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to dismiss alert:', error);
    }
  };

  // Get icon for alert type
  const getAlertIcon = (type) => {
    switch (type) {
      case 'stock_zero':
      case 'stock_critical':
        return <Package className="h-4 w-4" />;
      case 'task_overdue':
      case 'task_due_today':
        return <Clock className="h-4 w-4" />;
      case 'batch_expiring':
        return <Calendar className="h-4 w-4" />;
      default:
        return <AlertTriangle className="h-4 w-4" />;
    }
  };

  if (!isAuthenticated) return null;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="relative"
          data-testid="alerts-button"
        >
          {unreadCount > 0 ? (
            <BellRing className="h-5 w-5 text-amber-500 animate-pulse" />
          ) : (
            <Bell className="h-5 w-5" />
          )}
          {unreadCount > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="flex items-center justify-between p-3 border-b">
          <div className="flex items-center gap-2">
            <BellRing className="h-4 w-4" />
            <span className="font-semibold">Alertas</span>
            {isConnected && (
              <span className="h-2 w-2 bg-green-500 rounded-full animate-pulse" title="Conectado" />
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSoundEnabled(!soundEnabled)}
            className="h-8 w-8 p-0"
            title={soundEnabled ? "Desativar som" : "Ativar som"}
          >
            {soundEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
          </Button>
        </div>
        
        <ScrollArea className="h-80">
          {alerts.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 text-muted-foreground">
              <Check className="h-8 w-8 mb-2 text-green-500" />
              <p className="text-sm">Nenhum alerta no momento</p>
            </div>
          ) : (
            <div className="divide-y">
              {alerts.map((alert, idx) => (
                <div 
                  key={idx}
                  className={`p-3 hover:bg-muted/50 transition-colors ${
                    alert.severity === 'critical' 
                      ? 'bg-red-50 dark:bg-red-900/20 border-l-4 border-l-red-500' 
                      : 'bg-amber-50 dark:bg-amber-900/20 border-l-4 border-l-amber-500'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`mt-0.5 ${
                      alert.severity === 'critical' ? 'text-red-500' : 'text-amber-500'
                    }`}>
                      {getAlertIcon(alert.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">{alert.title}</p>
                      <p className="text-xs text-muted-foreground truncate">{alert.message}</p>
                      {alert.timestamp && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(alert.timestamp).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => dismissAlert(alert)}
                      className="h-6 w-6 p-0 shrink-0"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
        
        {alerts.length > 0 && (
          <div className="p-2 border-t">
            <Button 
              variant="ghost" 
              size="sm" 
              className="w-full text-xs"
              onClick={() => {
                setAlerts([]);
                setUnreadCount(0);
              }}
            >
              Limpar todos
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default RealTimeAlerts;
